// Text helpers for the public share pages that must give exactly the same result as
// weavewords-api's ShareRenderHandler (share_render_handler.go), which renders the same title,
// description and noindex server-side for the first load. Change both sides together.
import { parseExplanation } from './explanationMetadata';

// Same as weavewords-api repository.MinIndexableCollectionWords: smaller collections are noindex.
export const MIN_INDEXABLE_COLLECTION_WORDS = 10;

// Code points Go's unicode.IsSpace treats as space (strings.TrimSpace / strings.Fields), which the
// server uses. String.trim differs: it keeps U+0085 and strips U+FEFF. Listed as numbers because
// U+2028/U+2029 can't appear literally in a regex.
const GO_SPACE = new Set([
    0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x20, 0x85, 0xa0, 0x1680,
    0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a,
    0x2028, 0x2029, 0x202f, 0x205f, 0x3000,
]);

// Go's strings.Fields: the runs of non-space characters.
function fields(s: string): string[] {
    const out: string[] = [];
    let word = '';
    for (const ch of s) {
        if (GO_SPACE.has(ch.codePointAt(0) ?? 0)) {
            if (word) out.push(word);
            word = '';
        } else {
            word += ch;
        }
    }
    if (word) out.push(word);
    return out;
}

// Go's strings.TrimSpace.
function trimLikeGo(s: string): string {
    const chars = Array.from(s);
    let start = 0;
    let end = chars.length;
    while (start < end && GO_SPACE.has(chars[start].codePointAt(0) ?? 0)) start++;
    while (end > start && GO_SPACE.has(chars[end - 1].codePointAt(0) ?? 0)) end--;
    return chars.slice(start, end).join('');
}

// Same as the API's joinTranslations: blank entries are dropped, so [''] reads as untranslated.
export function joinTranslations(translations: string[] | null | undefined): string {
    return (translations ?? []).map(trimLikeGo).filter(Boolean).join(', ');
}

// Same as the API's plain(): <br> becomes a space, other tags are dropped without a gap,
// entities are decoded, whitespace runs collapse to one space.
export function plainText(s: string): string {
    const noTags = s.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '');
    const decoder = document.createElement('textarea');
    decoder.innerHTML = noTags;
    return fields(decoder.value).join(' ');
}

// Longest description snippet, in code points (Go runes), before it is cut at a word boundary.
const SUMMARY_MAX = 150;

// The explanation's gist for a word page's description: its [Definition] if it has one, otherwise
// its free text — without the [Synonyms]/[Antonyms]/... header lines — as plain text, cut at a word
// boundary after SUMMARY_MAX code points. Same as the API's explanationSummary.
export function explanationSummary(explanation: string | undefined | null): string {
    const parsed = parseExplanation(explanation);
    const text = plainText(parsed.definition) || plainText(parsed.freeText);
    const chars = Array.from(text);
    if (chars.length <= SUMMARY_MAX) return text;
    const cut = chars.slice(0, SUMMARY_MAX).join('');
    const space = cut.lastIndexOf(' ');
    return (space > 0 ? cut.slice(0, space) : cut) + '…';
}

// The canonical form of a legacy numeric collection ID (/c/007 → "7"), or null when the server
// wouldn't treat `token` as one: the API's legacyCollectionID (1–10 digits) that also fits uint32.
export function canonicalLegacyId(token: string): string | null {
    if (!/^[0-9]{1,10}$/.test(token)) return null;
    const id = Number(token);
    return id <= 0xffffffff ? String(id) : null;
}

// Same formulas as the API's collectionPage.
export function collectionPageText(c: { name: string; language: string; description: string; item_count: number }): { title: string; description: string } {
    return {
        title: `${c.name} — ${c.language} vocabulary collection`,
        description: c.description
            ? `${c.description} · ${c.item_count} words`
            : `A ${c.language} vocabulary collection with ${c.item_count} words on WeaveWords.`,
    };
}

// Same formulas as the API's lessonPage, including its strings.Fields whitespace collapsing.
export function lessonPageText(l: { title: string; language_level?: string; language?: string; topic: string }): { title: string; description: string } {
    const description = fields(
        `${l.title} — an interactive ${l.language_level ?? ''} ${l.language ?? ''} vocabulary lesson on ${l.topic}, free on WeaveWords.`,
    ).join(' ');
    return { title: `${l.title} | WeaveWords`, description };
}

// Same formulas as the API's wordPage.
export function wordPageText(word: string, translation: string, explanation?: string | null): { title: string; description: string } {
    const summary = explanationSummary(explanation);
    if (!translation) {
        return {
            title: `${word} | WeaveWords`,
            description: summary ? `${word} — ${summary}` : `Learn "${word}" with spaced repetition on WeaveWords.`,
        };
    }
    return {
        title: `${word} — ${translation} | WeaveWords`,
        description: summary
            ? `${word} means "${translation}" — ${summary}`
            : `${word} means "${translation}" — learn it with spaced repetition on WeaveWords.`,
    };
}
